// src/modules/competitions/components/JudgeEvaluationModal.tsx

import React, { useState, useEffect } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import Loading from '../../../shared/components/Loading';
import ErrorMessage from '../../../shared/components/ErrorMessage';
import { judgeService } from '../services/judgeService';
import { 
  EvaluationCriterion, 
  CriterionEvaluation 
} from '../types/judge';

interface JudgeEvaluationModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: number;
  level: string;
  imageTypeId: number;
  imageTypeName: string;
  submissionId?: number;
  imageUrl?: string;           // ← URL optimizada desde el padre
  onEvaluationComplete: () => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  currentIndex?: number;
  totalItems?: number;
}

const JudgeEvaluationModal: React.FC<JudgeEvaluationModalProps> = ({
  isOpen,
  onClose,
  categoryId,
  level,
  imageTypeId,
  imageTypeName,
  submissionId,
  imageUrl,              // ← Recibido del padre
  onEvaluationComplete,
  onNavigate,
  currentIndex,
  totalItems
}) => {
  const [criteria, setCriteria] = useState<EvaluationCriterion[]>([]);
  const [submission, setSubmission] = useState<any>(null);
  const [evaluations, setEvaluations] = useState<Record<number, number>>({});
  const [comments, setComments] = useState<Record<number, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // ✅ Estado para controlar carga de imagen
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (submissionId) {
        loadSubmissionDetails();
      }
      // Reset image states when modal opens
      setImageLoaded(false);
      setImageError(false);
    }
  }, [isOpen, submissionId]);

  const loadSubmissionDetails = async () => {
  try {
    setIsLoading(true);
    setError(null);
    
    const response = await judgeService.getSubmissions(categoryId, level, imageTypeId);
    
    // ✅ LOG 1: Ver toda la respuesta
    // console.log('🔍 RESPONSE COMPLETA:', response);
    // console.log('🔍 Response.success:', response.success);
    // console.log('🔍 Response.data:', response.data);
    
    if (response.success && response.data) {
      // console.log('🔍 Submissions recibidas:', response.data.submissions);
      // console.log('🔍 Buscando submission_id:', submissionId);
      
      const foundSubmission = response.data.submissions.find(
        (s: any) => s.submission_id === submissionId
      );
      
      // ✅ LOG 2: Ver la submission encontrada
      // console.log('🔍 Found submission:', foundSubmission);
      
      if (foundSubmission) {
        // ✅ LOG 3: Ver si tiene evaluación
        // console.log('🔍 Has my_evaluation?', !!foundSubmission.my_evaluation);
        // console.log('🔍 my_evaluation:', foundSubmission.my_evaluation);
        
        setSubmission(foundSubmission);
        setCriteria(response.data.criteria);
        
        if (foundSubmission.my_evaluation) {
          // console.log('✅ Llamando loadExistingEvaluation con:', foundSubmission.my_evaluation);
          loadExistingEvaluation(foundSubmission.my_evaluation);
        } else {
          // console.log('⚠️ NO tiene my_evaluation - es una evaluación nueva');
        }
      } else {
        // console.log('❌ NO se encontró la submission');
        setError('No se encontró la imagen');
      }
    } else {
      // console.log('❌ Response no exitoso o sin data');
      setError(response.message || 'Error al cargar imagen');
    }
  } catch (err) {
    // console.log('❌ ERROR en loadSubmissionDetails:', err);
    setError('Error al cargar imagen para evaluar');
  } finally {
    setIsLoading(false);
  }
};

  const loadExistingEvaluation = (evaluation: any) => {
  console.log('📝 loadExistingEvaluation recibió:', evaluation);
  console.log('📝 criteria_evaluations:', evaluation.criteria_evaluations);
  
  const evalMap: Record<number, number> = {};
  const commentMap: Record<number, string> = {};
  
  evaluation.criteria_evaluations?.forEach((ce: CriterionEvaluation) => {
    console.log('📝 Procesando criterio:', ce);
    evalMap[ce.criterion_id] = ce.score;
    if (ce.comment) {
      commentMap[ce.criterion_id] = ce.comment;
    }
  });
  
  console.log('📝 evalMap final:', evalMap);
  console.log('📝 commentMap final:', commentMap);
  
  setEvaluations(evalMap);
  setComments(commentMap);
};

  const handleScoreChange = (criterionId: number, score: number) => {
    setEvaluations(prev => ({
      ...prev,
      [criterionId]: score
    }));
  };

  const handleCommentChange = (criterionId: number, comment: string) => {
    setComments(prev => ({
      ...prev,
      [criterionId]: comment
    }));
  };

  const validateEvaluation = (): boolean => {
    for (const criterion of criteria) {
      const score = evaluations[criterion.criterion_id];
      
      if (score === undefined || score === null) {
        setError(`Falta evaluar: ${criterion.name}`);
        return false;
      }
      
      if (score < criterion.min_score || score > criterion.max_score) {
        setError(`Puntaje inválido en: ${criterion.name}`);
        return false;
      }
    }
    
    return true;
  };

  const calculateTotalScore = (): number => {
    let total = 0;
    criteria.forEach(criterion => {
      const score = evaluations[criterion.criterion_id] || 0;
      total += score;
    });
    return total;
  };

  const handleSave = async (isFinal: boolean) => {
    if (!submission) return;
    
    if (!validateEvaluation()) return;
    
    try {
      setIsSaving(true);
      setError(null);
      
      const criteriaEvaluations: CriterionEvaluation[] = criteria.map(criterion => ({
        criterion_id: criterion.criterion_id,
        score: evaluations[criterion.criterion_id],
        comment: comments[criterion.criterion_id] || null
      }));

      const response = await judgeService.saveEvaluation({
        submission_id: submission.submission_id,
        criteria_evaluations: criteriaEvaluations,
        is_final: isFinal
      });

      if (response.success) {
        onEvaluationComplete();
        
        if (isFinal) {
          if (onNavigate && currentIndex && totalItems && currentIndex < totalItems) {
            setEvaluations({});
            setComments({});
          } else {
            onClose();
          }
        }
      } else {
        setError(response.message || 'Error al guardar evaluación');
      }
    } catch (err) {
      setError('Error al guardar evaluación');
    } finally {
      setIsSaving(false);
    }
  };

  // ✅ Handlers para carga de imagen
  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true); // Para ocultar el spinner
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Evaluación - ${imageTypeName}`}
      size="fullscreen"
    >
      {isLoading ? (
        <Loading message="Cargando imagen..." />
      ) : !submission ? (
        <div className="no-submissions">
          <p>No hay imágenes disponibles</p>
          <Button onClick={onClose}>Cerrar</Button>
        </div>
      ) : (
        <div className="evaluation-container">
          {/* Imagen con carga optimizada */}
          <div className="evaluation-image-section">
            <div className="evaluation-image-wrapper">
              {/* ✅ Loading spinner mientras carga */}
              {!imageLoaded && !imageError && (
                <div className="image-loading-overlay">
                  <div className="spinner"></div>
                  <p>Cargando imagen...</p>
                </div>
              )}

              {/* ✅ Mensaje de error si falla */}
              {imageError && (
                <div className="image-error-overlay">
                  <span className="error-icon">⚠️</span>
                  <p>Error al cargar imagen</p>
                </div>
              )}

              {/* ✅ Usa imageUrl del padre, NO del submission */}
              <img 
                src={imageUrl}
                alt={imageTypeName}
                className="evaluation-image"
                onLoad={handleImageLoad}
                onError={handleImageError}
                style={{
                  opacity: imageLoaded && !imageError ? 1 : 0,
                  transition: 'opacity 0.4s ease-in-out'
                }}
              />
            </div>
            
            <div className="evaluation-meta">
              <p>
                {currentIndex && totalItems ? (
                  <>Imagen {currentIndex} de {totalItems} | </>
                ) : null}
                Participante: <strong>{submission.participant_code}</strong>
              </p>
            </div>
          </div>

          {/* Formulario de evaluación */}
          <div className="evaluation-form-section">
            <h3>Criterios de Evaluación</h3>

            {error && <ErrorMessage message={error} type="error" onClose={() => setError(null)} />}

            <div className="criteria-list">
              {criteria.map((criterion, index) => (
                <div key={criterion.criterion_id} className="criterion-item">
                  <div className="criterion-header">
                    <h4>
                      {index + 1}️⃣ {criterion.name} 
                      ({criterion.min_score}-{criterion.max_score} pts)
                    </h4>
                    {criterion.description && (
                      <p className="criterion-description">{criterion.description}</p>
                    )}
                  </div>

                  <div className="criterion-input">
                    <label>Score:</label>
                    <input
                      type="number"
                      min={criterion.min_score}
                      max={criterion.max_score}
                      value={evaluations[criterion.criterion_id] || ''}
                      onChange={(e) => handleScoreChange(
                        criterion.criterion_id, 
                        Number(e.target.value)
                      )}
                      className="score-input"
                    />
                  </div>

                  <div className="criterion-comment">
                    <label>Comentarios:</label>
                    <textarea
                      value={comments[criterion.criterion_id] || ''}
                      onChange={(e) => handleCommentChange(
                        criterion.criterion_id,
                        e.target.value
                      )}
                      placeholder="Comentarios opcionales..."
                      rows={3}
                      className="comment-textarea"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="evaluation-total">
              <strong>Total: {calculateTotalScore()} puntos</strong>
            </div>

            {/* Acciones */}
            <div className="evaluation-actions">
              {onNavigate && (
                <Button
                  variant="outline"
                  onClick={() => onNavigate('prev')}
                  disabled={isSaving}
                >
                  ◀ Anterior
                </Button>
              )}

              <Button
                variant="secondary"
                onClick={() => handleSave(false)}
                loading={isSaving}
                disabled={isSaving}
              >
                Guardar Borrador
              </Button>

              <Button
                variant="primary"
                onClick={() => handleSave(true)}
                loading={isSaving}
                disabled={isSaving}
              >
                Finalizar ✅
              </Button>

              {onNavigate && (
                <Button
                  variant="outline"
                  onClick={() => onNavigate('next')}
                  disabled={isSaving}
                >
                  Siguiente ▶
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default JudgeEvaluationModal;